import get from 'lodash/get';
import { browserHistory } from 'react-router';
import { put, take, race, select, takeLatest } from 'redux-saga/effects';

import { updateDeep } from '../../utils/updateDeep';
import { PROGRAM_ERRORS } from '../../constants/errors';
import { SUBSCRIBABLE_TYPE, TODO_TYPES } from '../../constants/index';
import { PROGRAM_TYPE, PROGRAM_COUNTERS } from '../../constants/programs';
import {
  loggedUserIdSelector,
  targetUserIdSelector,
  targetUserSelector,
} from '../context/selectors';
import { selectScheduledTasks } from '../healthboard/selectors';
import {
  actions as accountVaultsActions,
  consts as accountVaultsConsts,
} from './accountVaults';
import { actions as accountVaultsFormActions } from './accountVaultsForm';
import {
  joinedProgramsListSelector,
  joinedProgramsMetaSelector,
  selectPaymentInfoModalInvitationId,
  selectPaymentInfoModalPrepayFlow,
  selectPaymentInfoModalProgramId,
  selectPaymentInfoModalProgramName,
  selectPaymentInfoModalPurchaseAgain,
  selectPaymentInfoModalToDoId,
  selectPaymentInfoModalUserId,
} from './selectors';

import { actions as errorActions } from '../ui/error';
import { consts as successConsts } from '../ui/success';
import { activeTodoActions } from '../healthboard/activeTodo';
import {
  PAYMENT_INFO_MODAL,
  paymentInfoModalActions,
} from './payment-info-modal';
import { actions as joinedProgramsActions } from './joinedPrograms';
import { actions as scheduledTasksActions } from '../healthboard/requests/scheduled-tasks-upcoming-req';
import {
  consts as leaveProgramConsts,
  actions as leaveProgramActions,
} from './leaveProgram';
import {
  consts as acceptConsentConsts,
  actions as acceptConsentActions,
} from './acceptConsent';
import {
  consts as acceptInvitationConsts,
  actions as acceptInvitationActions,
} from './acceptInvitation';
import {
  consts as declineInvitationConsts,
  actions as declineInvitationActions,
} from './declineInvitation';
import { consts as popupConsts } from '../ui/popup';
import { UI_POPUP_TYPES } from '../../constants/props';
import {
  actions as submitPaymentProgram,
  consts as submitPaymentProgramConsts,
} from './submitPaymentProgram';
import { currentLocationSelector } from '../common/selectors';

const ACTIVE = PROGRAM_COUNTERS[PROGRAM_TYPE.ACTIVE];
const HISTORY = PROGRAM_COUNTERS[PROGRAM_TYPE.HISTORY];
const INVITES = PROGRAM_COUNTERS[PROGRAM_TYPE.INVITES];
const { ALREADY_COMPLETED, INVITATION_REMOVED } = PROGRAM_ERRORS;

/** ============================================
 *  COMMON
 *  ============================================ */

export function* updateTodos(id) {
  const payload = { id, type: TODO_TYPES.PROGRAM };

  yield put(scheduledTasksActions.removeEntity(payload));
  yield put(activeTodoActions.setActiveItem(null));
}

export function* updatePrograms(id, [from, into]) {
  const programs = yield select(joinedProgramsListSelector);
  const counters = yield select(joinedProgramsMetaSelector);

  const list = programs.filter(program => program.id !== id);

  const meta = {
    ...counters,
    ...(into && { [into]: counters[into] + 1 }),
    ...(from && { [from]: counters[from] - 1 }),
  };

  yield put(joinedProgramsActions.succeed(list));
  yield put(joinedProgramsActions.addMeta(meta));
}

export function* handleError({ error, payload, purchaseAgain }) {
  const { code } = { ...get(error, 'payload.error') };
  const { todoId, programId, invitationId } = payload;

  if (code === ALREADY_COMPLETED) {
    if (todoId) yield updateTodos(invitationId);
    else
      yield updatePrograms(programId, [
        purchaseAgain ? HISTORY : INVITES,
        ACTIVE,
      ]);

    yield put(
      errorActions.update({
        id: 'program.invite.notification.completed',
      }),
    );
  }

  if (code === INVITATION_REMOVED) {
    if (todoId) yield updateTodos(invitationId);
    else yield updatePrograms(programId, [INVITES]);

    yield put(
      errorActions.update({
        id: 'program.invite.notification.removed',
      }),
    );
  }

  if (!code)
    yield put(
      errorActions.update({
        id: 'errors.common',
      }),
    );
}

/** ============================================
 *  LEAVE
 *  ============================================ */

function* leaveProgramWorker({ payload }) {
  const { programId } = payload;

  yield put(leaveProgramActions.send(payload));
  yield take(leaveProgramConsts.SUCCEED);

  yield updatePrograms(programId, [ACTIVE, HISTORY]);

  const mainUserId = yield select(loggedUserIdSelector);
  const targetUser = yield select(targetUserSelector);

  yield put({
    type: successConsts.UPDATE,
    ...(mainUserId === targetUser.id
      ? {
          payload: {
            id: 'programs.remove.message',
          },
        }
      : {
          payload: {
            id: 'programs.remove.message.ar',
          },
          values: {
            dependentName: targetUser.displayableName,
          },
        }),
  });
}

/** ============================================
 *  ACCEPT
 *  ============================================ */

function* acceptInvitationWorker({ payload }) {
  const {
    userId,
    todoId,
    programId,
    prepayFlow,
    programName,
    invitationId,
    purchaseAgain,
    duePaymentDate,
    organizationId,
    withSubscription,
    prepaidByProvider,
    subscriptionsSetting,
  } = payload;

  const mainUserId = yield select(loggedUserIdSelector);
  const targetUser = yield select(targetUserSelector);

  if (withSubscription && !prepaidByProvider) {
    yield put(
      accountVaultsActions.send({
        userId: mainUserId,
        subjectId: programId,
        subjectType: SUBSCRIBABLE_TYPE.PROGRAM,
      }),
    );

    const { successAccountVaults, errorAccountVaults } = yield race({
      successAccountVaults: take(accountVaultsConsts.SUCCEED),
      errorAccountVaults: take(accountVaultsConsts.REJECTED),
    });

    if (successAccountVaults && successAccountVaults.payload.length !== 0) {
      yield put(
        paymentInfoModalActions.open({
          subscriptionsSetting,
          programId,
          userId,
          todoId,
          prepayFlow,
          mainUserId,
          programName,
          invitationId,
          organizationId,
          purchaseAgain,
          duePaymentDate,
        }),
      );
    }

    if (successAccountVaults && successAccountVaults.payload.length === 0) {
      yield put(
        accountVaultsFormActions.send({ userId: mainUserId, programId }),
      );
      yield put(
        paymentInfoModalActions.open({
          subscriptionsSetting,
          programName,
          programId,
          userId,
          todoId,
          prepayFlow,
          mainUserId,
          invitationId,
          organizationId,
          purchaseAgain,
          duePaymentDate,
        }),
      );
      yield put({
        type: popupConsts.SHOW,
        payload: {
          contentType: UI_POPUP_TYPES.PAYA_ACCOUNT_FORM,
          contentOptions: {
            noCards: true,
          },
        },
      });
    }
    if (errorAccountVaults) {
      yield put(
        errorActions.update({
          id: 'errors.common',
        }),
      );
    }
  } else {
    yield put(acceptInvitationActions.send(payload));
  }

  const { error } = yield race({
    error: take(acceptInvitationConsts.REJECTED),
    success: take(acceptInvitationConsts.SUCCEED),
  });

  if (error) {
    yield handleError({ error, payload, purchaseAgain });
    return;
  }

  if (todoId) yield updateTodos(invitationId);
  else
    yield updatePrograms(programId, [
      purchaseAgain ? HISTORY : INVITES,
      ACTIVE,
    ]);

  yield put({
    type: successConsts.UPDATE,
    ...(mainUserId === userId
      ? {
          payload: {
            id: 'program.invite.notification.joined',
          },
        }
      : {
          payload: {
            id: 'program.invite.notification.joinedAR',
          },
          values: {
            dependentName: targetUser.displayableName,
          },
        }),
  });
}

/** ============================================
 *  DECLINE
 *  ============================================ */

function* declineInvitationWorker({ payload }) {
  const { todoId, programId, invitationId } = payload;

  yield put(declineInvitationActions.send(payload));

  const { error } = yield race({
    error: take(declineInvitationConsts.REJECTED),
    success: take(declineInvitationConsts.SUCCEED),
  });

  if (error) {
    yield handleError({ error, payload });
    return;
  }

  if (todoId) yield updateTodos(invitationId);
  else yield updatePrograms(programId, [INVITES]);

  yield put(
    errorActions.update({
      id: 'program.invite.notification.declined',
    }),
  );
}

/** ============================================
 *  CONSENTS
 *  ============================================ */

function* acceptConsentWorker({ payload }) {
  const userId = yield select(targetUserIdSelector);
  const { todoId: tId, programId: pId, consentId: cId } = payload;

  yield put(acceptConsentActions.send({ ...payload, userId }));
  const { payload: consent } = yield take(acceptConsentConsts.SUCCEED);

  if (tId) {
    const todos = yield select(selectScheduledTasks);
    const path = [{ id: tId }, 'payload', 'consents', { id: cId }];
    const list = updateDeep(todos, path, () => consent);
    yield put(scheduledTasksActions.succeed(list));
  } else {
    const programs = yield select(joinedProgramsListSelector);
    const counters = yield select(joinedProgramsMetaSelector);
    const path = [{ id: pId }, 'patientInvitation', 'consents', { id: cId }];
    const list = updateDeep(programs, path, () => consent);
    yield put(joinedProgramsActions.succeed(list));
    yield put(joinedProgramsActions.addMeta(counters));
  }
}

/** ============================================
 *  PAYA PAYMENT SAGA
 *  ============================================ */

function* payaProgramPaymentSaga() {
  const mainUserId = yield select(loggedUserIdSelector);
  const targetUser = yield select(targetUserSelector);

  const { success, error } = yield race({
    success: take(submitPaymentProgramConsts.SUCCEED),
    error: take([
      submitPaymentProgramConsts.REJECTED,
      submitPaymentProgramConsts.ERROR_ENCOUNTERED,
    ]),
  });

  const todoId = yield select(selectPaymentInfoModalToDoId);
  const programId = yield select(selectPaymentInfoModalProgramId);
  const userId = yield select(selectPaymentInfoModalUserId);
  const invitationId = yield select(selectPaymentInfoModalInvitationId);
  const purchaseAgain = yield select(selectPaymentInfoModalPurchaseAgain);
  const prepayFlow = yield select(selectPaymentInfoModalPrepayFlow);
  const pathname = yield select(currentLocationSelector);

  if (error) {
    yield handleError({ error, payload: { programId, userId, todoId } });
    yield put(paymentInfoModalActions.clearWaitingPayaPaymentResponse());
    return;
  }

  if (success) {
    const { succeed, rejected } = yield race({
      succeed: take(PAYMENT_INFO_MODAL.RECEIVED_PAYA_PAYMENT_SUCCESS_RESPONSE),
      rejected: take(PAYMENT_INFO_MODAL.RECEIVED_PAYA_PAYMENT_ERROR_RESPONSE),
    });

    yield put(paymentInfoModalActions.clearWaitingPayaPaymentResponse());

    if (succeed) {
      const programName = yield select(selectPaymentInfoModalProgramName);

      if (todoId) yield updateTodos(invitationId);
      else if (!prepayFlow)
        yield updatePrograms(programId, [
          purchaseAgain ? HISTORY : INVITES,
          ACTIVE,
        ]);

      if (purchaseAgain)
        browserHistory.push({ pathname, search: '?tab=active' });

      yield put(paymentInfoModalActions.close());
      if (prepayFlow) {
        yield put(
          joinedProgramsActions.send({
            id: userId,
            filterBy: PROGRAM_TYPE.ACTIVE,
          }),
        );
        yield put({
          type: successConsts.UPDATE,
          ...(mainUserId === userId
            ? {
                payload: {
                  id: 'program.membership.prepay.success',
                },
              }
            : {
                payload: {
                  id: 'program.membership.prepay.dependent.success',
                },
                values: {
                  dependentName: targetUser.displayableName,
                },
              }),
        });
      } else {
        yield put({
          type: popupConsts.SHOW,
          payload: {
            contentType: UI_POPUP_TYPES.PAYA_PAYMENT_HANDLE_RESPONSE,
            contentOptions: {
              success: true,
              programName,
              ...(userId !== mainUserId && {
                dependentName: targetUser.displayableName,
              }),
            },
          },
        });
      }
    } else {
      yield put({
        type: popupConsts.SHOW,
        payload: {
          contentType: UI_POPUP_TYPES.PAYA_PAYMENT_HANDLE_RESPONSE,
          contentOptions: {
            success: false,
            errorMessage: rejected.payload.errorMessage,
          },
        },
      });
    }
  }
}

export function* programSagas() {
  yield takeLatest([leaveProgramConsts.INIT], leaveProgramWorker);
  yield takeLatest([acceptConsentConsts.INIT], acceptConsentWorker);
  yield takeLatest([acceptInvitationConsts.INIT], acceptInvitationWorker);
  yield takeLatest([declineInvitationConsts.INIT], declineInvitationWorker);
  yield takeLatest([submitPaymentProgram.send], payaProgramPaymentSaga);
}



// WEBPACK FOOTER //
// ./src/redux/programs/programSagas.js